import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";

import { COMMON_COPY } from "copy/common";
import { getActiveLocale } from "i18n/activeLocale";
import { resolveCopyText } from "i18n/resolveCopy";

import { UserService } from "../services/UserService";
import { isAuthenticationError } from "../types/errors/ApiErrors";

/**
 * This hook is responsible for initializing the user session once after login.
 * It calls the backend verification endpoint to ensure the user is synced.
 */
export const useUserSessionInitializer = () => {
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const initializeSession = async () => {
      if (isAuthenticated && !isInitialized) {
        setIsLoading(true);
        try {
          const authToken = await getAccessTokenSilently();
          await UserService.verifyUserAsync(authToken);
          setIsInitialized(true); // Mark as initialized to prevent re-running
        } catch (caught) {
          console.error("Failed to initialize user session:", caught);

          // If it's an authentication error or Auth0 session error, redirect to login
          if (isAuthenticationError(caught)) {
            console.log("Session expired or authentication failed, redirecting to login...");
            await loginWithRedirect({
              appState: { returnTo: window.location.pathname + window.location.search },
            });
            return;
          }

          // Any other error (including BackendError): show backend unavailable page.
          // User can retry, return home, or sign out from that page.
          if (caught instanceof Error) {
            setError(caught);
          } else {
            setError(
              new Error(
                resolveCopyText({ text: COMMON_COPY.session.unknownInitializationError, locale: getActiveLocale() })
              )
            );
          }
        } finally {
          setIsLoading(false);
        }
      }
    };

    initializeSession();
  }, [isAuthenticated, isInitialized, getAccessTokenSilently, loginWithRedirect]);

  return { isInitialized, isLoading, error };
};
